/**
 * Main Layout - Sidebar + Header + Content
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { SchedulerBanner } from '@/features/smswiser-catalog/components/SchedulerBanner';
import { CatalogRefreshBanner } from '@/features/smswiser-catalog/components/refresh-job';
import { usePresenceHeartbeat } from '@/hooks/presence';
import { useAppEnv } from '@/features/app-env/useAppEnv';

export function MainLayout() {
  // Every authenticated screen sits inside this layout, so one heartbeat covers the app.
  usePresenceHeartbeat(true);
  // The reporting currency arrives with /app-env — loaded once here so every screen shows money in it.
  useAppEnv();

  const location = useLocation();
  const storageKey = 'trafficpulse:sidebar-collapsed';
  const initialCollapsed = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(storageKey) === '1';
  }, []);

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(initialCollapsed);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, sidebarCollapsed ? '1' : '0');
    } catch {
      // ignore storage errors
    }
  }, [sidebarCollapsed]);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar collapsed={sidebarCollapsed} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed((v) => !v)}
        />
        <main className="flex min-h-0 flex-1 flex-col min-w-0 overflow-y-auto overflow-x-hidden bg-muted/30">
          <SchedulerBanner />
          <CatalogRefreshBanner />
          <div className="flex flex-col p-6">
            {/* Key on pathname only: query-string changes (tabs, filters, pagination)
                must NOT remount the page, or query-driven UI state is lost. */}
            <Outlet key={location.pathname} />
          </div>
        </main>
      </div>
    </div>
  );
}
